<template>
  <div class="text-xs-center">
    <v-dialog v-model="dialog" fullscreen persistent>
      <v-card class="risk-assessment scroll-form" height="100%">
        <v-card-title
          class="headline indigo darken-4 white--text fixed-header"
          primary-title
        >
          {{
            $vuetify.t('$vuetify.powra.pointOfWorkRiskAssessment').toUpperCase()
          }}
        </v-card-title>
        <v-layout row wrap>
          <PowraTitle
            :avatar="'H'"
            :title="$vuetify.t('$vuetify.powra.carriedOutBy')"
          />

          <v-flex xs4 class="blue-grey lighten-5">
            <v-card-text>
              <strong>{{ $vuetify.t('$vuetify.powra.name') }}:</strong>
            </v-card-text>
          </v-flex>

          <v-flex xs8 class="blue-grey lighten-5">
            <v-card-text v-if="user">
              {{ user.full_name }}
            </v-card-text>
          </v-flex>

          <v-flex xs4>
            <v-card-text>
              <strong>{{ $vuetify.t('$vuetify.powra.position') }}:</strong>
            </v-card-text>
          </v-flex>

          <v-flex xs8>
            <v-card-text v-if="user">
              Mobile Engineer
            </v-card-text>
          </v-flex>

          <v-flex xs4 class="blue-grey lighten-5">
            <v-card-text>
              <strong>{{ $vuetify.t('$vuetify.powra.sign') }}:</strong>
            </v-card-text>
          </v-flex>

          <v-flex xs8 class="blue-grey lighten-5 text-xs-left">
            <v-card-text>
              <div v-if="currentSignature">
                Current signature
                <v-img :src="currentSignature" class="white lighten-2 media">
                  <v-layout
                    slot="placeholder"
                    fill-height
                    align-center
                    justify-center
                    ma-0
                  >
                    <v-progress-linear
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-linear>
                  </v-layout>
                </v-img>
              </div>

              <signature-pad
                buttonText="Signature"
                v-on:signature-updated="signatureUpdated"
                :callId="callDetail.id"
                title="Sign the Document"
                signatureType="signoff_signature"
                :onlyEvent="true"
              ></signature-pad>
            </v-card-text>
          </v-flex>

          <v-flex xs4>
            <v-card-text>
              <strong>{{ $vuetify.t('$vuetify.powra.date') }}:</strong>
            </v-card-text>
          </v-flex>

          <v-flex xs8>
            <v-card-text>
              {{ moment().format('DD/MM/YYYY') }}
            </v-card-text>
          </v-flex>
        </v-layout>

        <div class="footer text-xs-center mb-3">
          <v-divider></v-divider>
          <v-card-text>
            <v-btn @click="back()">
              {{ $vuetify.t('$vuetify.calls.back') }}
            </v-btn>

            <v-btn color="success" @click="confirm()">
              {{
                this.hasPowra
                  ? $vuetify.t('$vuetify.calls.save')
                  : $vuetify.t('$vuetify.calls.proceedToTheNextSection')
              }}
            </v-btn>
            <abort-visit v-on:aborted="visitAborted()" />
          </v-card-text>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog" width="500">
      <v-card>
        <v-card-title class="headline red white--text" primary-title>
          {{ $vuetify.t('$vuetify.permissionToWork.checkSignature') }}
        </v-card-title>
        <v-card-text>
          <v-container pa-1>
            <v-layout row>
              <v-flex shrink>
                <v-icon color="red" x-large>warning</v-icon>
              </v-flex>
              <v-flex pl-3>
                {{ $vuetify.t('$vuetify.permissionToWork.addSignature') }}
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="errorDialog = false">
            {{ $vuetify.t('$vuetify.app.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmationDialog" width="500">
      <v-card>
        <v-card-title
          v-if="user"
          class="headline red white--text"
          primary-title
        >
          {{ user.full_name }}
        </v-card-title>
        <v-card-text>
          <v-container pa-1>
            <v-layout row>
              <v-flex shrink>
                <v-icon color="red" x-large>warning</v-icon>
              </v-flex>
              <v-flex pl-3>
                {{ $vuetify.t('$vuetify.saveNewPowra.confirmationMessage') }}
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" flat @click="confirmationDialog = false">
            {{ $vuetify.t('$vuetify.app.no') }}
          </v-btn>
          <v-btn color="green" flat @click="submitPowra()">
            {{ $vuetify.t('$vuetify.app.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AbortVisit from '@/components/AbortVisit.vue'
import PowraTitle from '@/components/powra/PowraTitle.vue'
import { mapGetters, mapState } from 'vuex'
import signaturePad from '@/components/SignaturePad.vue'
import moment from 'moment'

import helpers from '@/utils/helpers'

export default {
  props: ['show', 'call'],
  components: {
    AbortVisit,
    signaturePad,
    PowraTitle
  },
  data() {
    return {
      dialog: false,
      signature: null,
      errorDialog: false,
      confirmationDialog: false
    }
  },
  computed: {
    ...mapGetters({
      getActiveCall: 'calls/getActiveCall',
      getSignature: 'calls/getSignature',
      getSignatureByType: 'calls/getSignatureByType',
      currentRiskAssessment: 'calls/currentRiskAssessment',
      user: 'root/user',
      hasPowra: 'calls/hasPowra'
    }),
    callDetail() {
      return this.getActiveCall
    },
    currentSignature() {
      // const s = this.getSignatureByType(this.callDetail.id, 'approved_by_signature')
      // console.log(this.currentRiskAssessment.signoff_signature, 'current signature off')
      let imgSource = this.signature
        ? this.signature.src
        : this.currentRiskAssessment
        ? this.currentRiskAssessment.signoff_signature
        : null
      if (imgSource && imgSource.indexOf('data:image/png;base64,') < 0) {
        imgSource = 'data:image/png;base64,' + imgSource
      }
      return imgSource
    }
  },
  methods: {
    confirm() {
      if (!this.currentSignature) {
        this.errorDialog = true
        return
      }
      if (this.user.full_name && this.user.position && this.currentSignature) {
        this.confirmationDialog = true
      }
    },
    submitPowra() {
      this.confirmationDialog = false

      if (this.user.full_name && this.user.position && this.currentSignature) {
        this.emitConfirmed()
      }
    },
    emitConfirmed() {
      const mediaArray = this.callDetail.media.data || this.callDetail.media
      const signatureMedia = _.last(mediaArray)
      this.$emit('confirmed', {
        name: this.user.full_name,
        position: this.user.position,
        // signature: signatureMedia.url,
        signoff_signature: this.currentSignature ? this.currentSignature : null,
        date: moment().format('DD/MM/YYYY')
      })
    },
    visitAborted() {
      this.dialog = false
      this.$emit('aborted')
    },
    back() {
      this.dialog = false
      this.$emit('back')
    },
    async signatureUpdated(p) {
      const maxWidth = process.env.VUE_APP_IMG_MAX_WIDTH
      const maxHeight = process.env.VUE_APP_IMG_MAX_HEIGHT
      const resizedImage = Object.assign({}, p)
      const res = await helpers.resizeImageFromBase64(
        p.src,
        maxWidth,
        maxHeight
      )
      resizedImage.src = res
      console.log('SIGNATURE updated', resizedImage, this.currentSignature)
      this.signature = resizedImage
    },
    moment: function() {
      return moment()
    }
  },
  watch: {
    show: function(newVal, oldVal) {
      this.dialog = newVal
    }
  }
}
</script>

<style lang="scss">
.risk-assessment .v-dialog__activator,
.risk-assessment .v-dialog__container {
  text-align: left !important;
}

.footer {
  position: -webkit-sticky;
  position: sticky;
  background-color: #ffffff;
  bottom: 0;
  width: 100%;
  height: 100px;
}

.fixed-header {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 300;
}

.scroll-form {
  height: 100%;
  position: relative;
  overflow-y: scroll;
}
</style>
